import * as React from 'react';

import { Badge, Collapse, Divider, Group, Image, List, Modal, Stack, Text, UnstyledButton, VisuallyHidden } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronRight } from '@tabler/icons-react';
import Markdown from 'markdown-to-jsx';

import logoURL from '../assets/bio-rad-logo.svg';
import { APP_NAME, APP_STATUS, APP_TAGLINE, SHOW_BUILD_DETAILS } from '../constants/constants';
import { DATE_FORMAT_OPTIONS } from '../constants/date-constants';
import { useGetBuildVersion } from '../store/store';
import { formatDate } from '../utils';

/**
 * The release notes markdown from RELEASE_NOTES.md is rendered in the dialog.
 * Only the elements the notes actually use are mapped,
 */
const RELEASE_NOTES_MARKDOWN_OPTIONS = {
  disableParsingRawHTML: true,
  overrides: {
    h3: { component: Text, props: { size: 'sm', fw: 500, mt: 'sm' } },
    ul: { component: List, props: { size: 'sm', c: 'dimmed', withPadding: true } },
    li: { component: List.Item },
    hr: { component: Divider, props: { my: 'xs' } },
  },
} as const;

interface AppAboutMenuProps {
  opened: boolean;
  onClose: () => void;
}

export function AppAboutMenu({ opened, onClose }: AppAboutMenuProps) {
  const { data: buildVersion } = useGetBuildVersion(undefined, { skip: !opened });
  const [notesOpened, { toggle: toggleNotes }] = useDisclosure(false);
  const buildDate = buildVersion?.buildDate ? formatDate(buildVersion.buildDate, DATE_FORMAT_OPTIONS.FULL_DATE) : undefined;
  const releaseNotes = buildVersion?.releaseNotes?.trim() ?? '';

  return (
    <Modal opened={opened} onClose={onClose} centered title={<VisuallyHidden>About {APP_NAME}</VisuallyHidden>}>
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Image h={26} w="auto" src={logoURL} alt="Bio-Rad" />
          <Badge color="brand" variant="light" radius="xl" tt="none">
            {APP_STATUS}
          </Badge>
        </Group>

        <Stack gap={4}>
          <Text size="lg" fw={600}>
            {APP_NAME}
          </Text>
          <Text size="sm" c="dimmed">
            {APP_TAGLINE}
          </Text>
        </Stack>

        <Divider />

        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase">
            Version
          </Text>
          <Text fz="h2" fw={700} data-testid="app-version">
            {buildVersion?.version ?? '—'}
          </Text>
          {buildDate && (
            <Text size="sm" c="dimmed" data-testid="app-build-date">
              {buildDate}
            </Text>
          )}
        </Stack>

        {releaseNotes && (
          <Stack gap="xs">
            <UnstyledButton onClick={toggleNotes} aria-expanded={notesOpened} data-testid="release-notes-toggle">
              <Group gap={6} align="center">
                <IconChevronRight
                  size={14}
                  style={{
                    transition: 'transform 0.15s ease',
                    transform: notesOpened ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                />
                <Text size="sm" fw={500}>
                  Release notes
                </Text>
              </Group>
            </UnstyledButton>

            <Collapse in={notesOpened}>
              <Stack gap="sm" data-testid="release-notes">
                <Markdown options={RELEASE_NOTES_MARKDOWN_OPTIONS}>{releaseNotes}</Markdown>
              </Stack>
            </Collapse>
          </Stack>
        )}

        {SHOW_BUILD_DETAILS && buildVersion && (
          <Text size="xs" c="dimmed" ff="monospace" data-testid="app-build-details">
            {buildVersion.branch} · build {buildVersion.buildNumber} · {buildVersion.sha1.slice(0, 12)}
          </Text>
        )}
      </Stack>
    </Modal>
  );
}
